import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Chip, Menu, MenuItem, Stack } from "@mui/material";
import { Button } from "@platform/design-system";
import {
  type BenefitPlanGroup,
  coverageTypeToLabel,
} from "@platform/btx-plan-designer";
import { isUnmappedPageId } from "@platform/mpx-quotes-grid";
import { CoverageTypeEnum } from "@platform/types";
import { useCallback, useMemo, useState } from "react";

import { getBenefitPlanGroupLabel } from "../utils/benefitPlanGroupLabel";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Small count badge (btx-unmapped-plans) shown before a LOC or "Unmapped Plans" label. */
const CountBadge = ({ count }: { count: number }): React.JSX.Element =>
  count > 0 ? (
    <Chip
      label={count}
      size="small"
      color="brandPrimary"
      sx={{ minWidth: 25, fontSize: "0.7rem", borderRadius: 1 }}
    />
  ) : null;

export interface CoverageBenefitPlanSelectorProps {
  benefitPlanGroupsByCoverage: Partial<
    Record<CoverageTypeEnum, BenefitPlanGroup[]>
  >;
  availableCoverageTypes: CoverageTypeEnum[];
  isLoadingAvailableCoverageTypes: boolean;
  /** Count of unmapped carrier plan groups per coverage (btx-unmapped-plans), for badges. */
  unmappedCountByCoverage?: Partial<Record<CoverageTypeEnum, number>>;
  selectedCoverageType?: CoverageTypeEnum;
  selectedBpgId?: string;
  onSelect: (coverageType: CoverageTypeEnum, bpgId?: string) => void;
}

export const CoverageBenefitPlanSelector = ({
  benefitPlanGroupsByCoverage,
  availableCoverageTypes,
  isLoadingAvailableCoverageTypes,
  unmappedCountByCoverage = {},
  selectedCoverageType,
  selectedBpgId,
  onSelect,
}: CoverageBenefitPlanSelectorProps): React.JSX.Element | null => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [hoveredCoverageType, setHoveredCoverageType] =
    useState<CoverageTypeEnum | null>(null);

  const buttonLabel = useMemo(() => {
    if (isLoadingAvailableCoverageTypes && !selectedCoverageType)
      return "Loading...";
    if (selectedCoverageType) {
      const selectedGroup = benefitPlanGroupsByCoverage[
        selectedCoverageType
      ]?.find((group) => group.id === selectedBpgId);

      if (selectedGroup) {
        return `${coverageTypeToLabel(selectedCoverageType)} / ${getBenefitPlanGroupLabel(selectedGroup)}`;
      }

      return coverageTypeToLabel(selectedCoverageType);
    }
    return "Select Plan";
  }, [
    benefitPlanGroupsByCoverage,
    isLoadingAvailableCoverageTypes,
    selectedBpgId,
    selectedCoverageType,
  ]);

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setSubmenuAnchorEl(null);
    setHoveredCoverageType(null);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSubmenuAnchorEl(null);
    setHoveredCoverageType(null);
  }, []);

  const hoveredBenefitPlanGroups = hoveredCoverageType
    ? (benefitPlanGroupsByCoverage[hoveredCoverageType] ?? [])
    : [];

  const handleCoverageHover = useCallback(
    (event: React.MouseEvent<HTMLElement>, coverageType: CoverageTypeEnum) => {
      const benefitPlanGroups = benefitPlanGroupsByCoverage[coverageType] ?? [];

      if (benefitPlanGroups.length === 0) {
        setSubmenuAnchorEl(null);
        setHoveredCoverageType(null);
        return;
      }

      setSubmenuAnchorEl(event.currentTarget);
      setHoveredCoverageType(coverageType);
    },
    [benefitPlanGroupsByCoverage],
  );

  const handleSubmenuClose = useCallback(() => {
    setSubmenuAnchorEl(null);
    setHoveredCoverageType(null);
  }, []);

  if (!isLoadingAvailableCoverageTypes && availableCoverageTypes.length === 0)
    return null;

  return (
    <>
      <Button
        data-testid="coverage-benefit-plan-selector-trigger"
        variant="secondary"
        size="sm"
        endIcon={<ExpandMoreIcon />}
        onClick={handleOpen}
        disabled={
          isLoadingAvailableCoverageTypes && availableCoverageTypes.length === 0
        }
      >
        {buttonLabel}
      </Button>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {availableCoverageTypes.map((type) => {
          const hasBpgs = (benefitPlanGroupsByCoverage[type] ?? []).length > 0;
          const unmappedCount = unmappedCountByCoverage[type] ?? 0;

          return (
            <MenuItem
              key={type}
              selected={
                hoveredCoverageType
                  ? type === hoveredCoverageType
                  : type === selectedCoverageType
              }
              onMouseEnter={(event) => handleCoverageHover(event, type)}
              onClick={(event) => {
                if (!hasBpgs) {
                  onSelect(type);
                  handleClose();
                  return;
                }

                // Also open the submenu on click so keyboard (Enter/Space) and
                // touch users can reach the plan groups, not just hover.
                handleCoverageHover(event, type);
              }}
              sx={{
                minWidth: 240,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                {<CountBadge count={unmappedCount} />}
                {coverageTypeToLabel(type)}
              </Stack>
              {hasBpgs && <ChevronRightIcon fontSize="small" color="action" />}
            </MenuItem>
          );
        })}
      </Menu>

      <Menu
        anchorEl={submenuAnchorEl}
        open={
          Boolean(anchorEl) &&
          Boolean(submenuAnchorEl) &&
          hoveredBenefitPlanGroups.length > 0
        }
        onClose={handleSubmenuClose}
        anchorOrigin={{ horizontal: "right", vertical: "top" }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        // Let pointer events pass through the submenu's modal overlay so hovering
        // another coverage type still opens its submenu; the paper re-enables
        // events for the submenu's own items.
        slotProps={{
          root: { sx: { pointerEvents: "none" } },
          paper: { sx: { pointerEvents: "auto" } },
        }}
      >
        {hoveredBenefitPlanGroups.map((group) => {
          const unmappedCount =
            isUnmappedPageId(group.id) && hoveredCoverageType
              ? (unmappedCountByCoverage[hoveredCoverageType] ?? 0)
              : 0;

          return (
            <MenuItem
              key={group.id}
              selected={
                hoveredCoverageType === selectedCoverageType &&
                group.id === selectedBpgId
              }
              onClick={() => {
                if (!hoveredCoverageType) {
                  return;
                }

                onSelect(hoveredCoverageType, group.id);
                handleClose();
              }}
              sx={{ minWidth: 240 }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                {<CountBadge count={unmappedCount} />}
                {getBenefitPlanGroupLabel(group)}
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
